import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import MessageReference from './MessageReference';
import { formatTaskWhen } from '../utils/taskTime';   // fix(task-dates): the shared formatter
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast } from '../utils/toast';        // Block 233
import { Banner, Button, Checkbox, ConfirmDialog, Sheet, SidePanel, StatusPill, useBreakpoint } from '../ui';
import './notifications.css';

/**
 * EventProposalsBanner
 *
 * Shared "Aidey spotted these in your emails" banner for email-detected event
 * proposals (Block 53/55). Self-contained: loads the workspace-scoped
 * /api/event-proposals endpoint, so it automatically shows only the active
 * workspace's proposals — the same component works in Family and Business views.
 *
 * Renders nothing when there are no pending proposals, and a problem banner with
 * the server's reason when they could not be read (Block J: a failed load rendered
 * nothing, which looked exactly like "none waiting").
 *
 * Block J (UI-22): restyled on the v2 kit; the bulk dismiss confirm is the kit's
 * ConfirmDialog.
 *
 * Fresh-eyes 16 (Carla, 2026-10-12): the suggestions no longer fill the page. They collapse
 * into ONE slim banner, "11 events spotted in your emails · Review" (the real count, the noun
 * agreeing), and Review opens them in the kit's SidePanel (a Sheet on a phone). The same on
 * every page that shows this banner (Calendar, the Family calendar, Notifications).
 *
 * Props:
 *   onChange?() — called after a proposal is accepted/resolved (host may refresh).
 *                 A global `aidey:tasksUpdated` event is also dispatched so any
 *                 calendar/task view listening for it re-fetches.
 */

// A date-only "2026-10-12" is a calendar day, never UTC midnight (which is the day before west of UTC).
function eventDay(value, opts) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', opts);
}

export default function EventProposalsBanner({ onChange }) {
  const [proposals, setProposals] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy]           = useState(null);   // proposal id mid-action
  const [reschedule, setReschedule] = useState({});   // { [id]: { suggestedStart, existingTitle } | 'noslot' }
  const [selected, setSelected]   = useState(() => new Set()); // Block 208: bulk dismiss
  const [bulkBusy, setBulkBusy]   = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  // Block 218: superseded rows. Retired by a later confirmation in the same thread —
  // model-driven, so it has to be reversible and visible.
  const [superseded, setSuperseded]   = useState([]);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [restoring, setRestoring]     = useState(null);
  const [panelOpen, setPanelOpen]     = useState(false);   // Fresh-eyes 16: the Review panel
  const phone = useBreakpoint() === 'phone';

  const load = useCallback(async () => {
    try {
      const res = await api('/api/event-proposals');
      const data = await res.json();
      setProposals(Array.isArray(data) ? data : []);
      setLoadError(null);
    } catch (err) {
      setLoadError(messageFromError(err, 'Suggestions from your email could not be read.'));
    }
  }, []);

  const loadSuperseded = useCallback(async () => {
    try {
      const res = await api('/api/event-proposals/superseded');
      const d = await res.json();
      setSuperseded(Array.isArray(d.proposals) ? d.proposals : []);
    } catch { /* non-fatal: the pending list above is what needs acting on */ }
  }, []);

  useEffect(() => { load(); loadSuperseded(); }, [load, loadSuperseded]);

  async function restore(id) {
    setRestoring(id);
    try {
      await api('/api/event-proposals/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] }),
      });
      await load(); await loadSuperseded(); notifyChanged();
    } catch (err) { showErrorToast(messageFromError(err, "Could not restore that suggestion.")); }
    finally { setRestoring(null); }
  }

  const drop = (id) => {
    setProposals(prev => prev.filter(p => p.id !== id));
    setReschedule(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const notifyChanged = () => {
    if (typeof onChange === 'function') onChange();
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('aidey:tasksUpdated'));
  };

  async function accept(p) {
    setBusy(p.id);
    try {
      const res = await api(`/api/event-proposals/${p.id}/accept`, { method: 'POST' });
      const data = await res.json();
      if (data?.requiresResolution) return; // clash UI (from clash_result) drives resolution
      drop(p.id);
      notifyChanged();
    } catch (err) {
      // Keeping the card on failure was right; saying nothing was not — the card just
      // sat there and the user clicked Accept again.
      showErrorToast(messageFromError(err, 'Could not add that to your calendar.'));
    }
    finally { setBusy(null); }
  }

  async function resolve(p, action, confirm = false) {
    setBusy(p.id);
    try {
      const res = await api(`/api/event-proposals/${p.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, confirm }),
      });
      const data = await res.json();
      if (data?.preview) {
        setReschedule(prev => ({ ...prev, [p.id]: { suggestedStart: data.suggestedStart, existingTitle: data.existingTitle } }));
        return;
      }
      if (data?.noSlotFound) {
        setReschedule(prev => ({ ...prev, [p.id]: 'noslot' }));
        return;
      }
      drop(p.id);
      notifyChanged();
    } catch (err) { showErrorToast(messageFromError(err, "Could not resolve that clash.")); }
    finally { setBusy(null); }
  }

  // Block 208: bulk dismiss. With no endpoint behind it, 47 proposals meant 47 clicks.
  // Sends the selected ids, or omits ids entirely to clear everything visible. The
  // ConfirmDialog shows a rejection's message inside the dialog, so this rethrows.
  async function dismissMany(ids) {
    setBulkBusy(true);
    try {
      await api('/api/event-proposals/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids ? { ids } : {}),
      });
      setProposals(prev => (ids ? prev.filter(p => !ids.includes(p.id)) : []));
      setSelected(new Set());
      notifyChanged();
    } catch (err) {
      throw new Error(messageFromError(err, "Could not dismiss those suggestions."));
    } finally { setBulkBusy(false); }
  }

  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allSelected = proposals.length > 0 && selected.size === proposals.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(proposals.map(p => p.id)));

  if (loadError) {
    return (
      <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={load}>Try again</Button>}>
        Suggestions Aidey found in your email could not be read: {loadError}
      </Banner>
    );
  }

  // Block 218: still render when there are no pending rows but some superseded ones,
  // otherwise a wrongly superseded proposal would be unreachable — the exact failure
  // the restore path exists to prevent.
  if (proposals.length === 0 && superseded.length === 0) return null;

  const dismissCount = selected.size || proposals.length;
  const n = proposals.length;
  // Fresh-eyes 16: ONE slim line, the real count, the noun agreeing (decision 87).
  const summary = n > 0
    ? `${n} event${n === 1 ? '' : 's'} spotted in your emails`
    : `${superseded.length} suggestion${superseded.length === 1 ? '' : 's'} from your emails replaced by a later one`;

  const body = (
    <div className="ep">
      {n > 0 && (
        <div className="ep__head">
          {/* Block 208: select-all + bulk dismiss. */}
          <Checkbox checked={allSelected} onChange={toggleAll} label="Select all" />
          {/* Block 208: bulk dismiss is irreversible — dismissed proposals are not
              recoverable through any UI, so it is a two-step confirm. */}
          <Button size="sm" variant="tertiary" disabled={bulkBusy} onClick={() => setConfirmBulk(true)}>
            {selected.size ? `Dismiss ${selected.size} selected` : `Dismiss all ${n}`}
          </Button>
        </div>
      )}

      <div className="ep__list">
        {proposals.map(p => (
          <ProposalCard
            key={p.id}
            p={p}
            busy={busy === p.id}
            selected={selected.has(p.id)}
            onToggle={() => toggleOne(p.id)}
            rs={reschedule[p.id]}
            onAccept={() => accept(p)}
            onResolve={(action, confirm) => resolve(p, action, confirm)}
            onCancelMove={() => setReschedule(prev => { const nx = { ...prev }; delete nx[p.id]; return nx; })}
          />
        ))}
      </div>

      {/* Block 218: superseded rows, reachable and reversible. Collapsed by default: the
          point is that the list got shorter, not to hand the noise straight back. */}
      {superseded.length > 0 && (
        <div className="ep__superseded">
          <Button size="sm" variant="tertiary" aria-expanded={showSuperseded} onClick={() => setShowSuperseded(v => !v)}>
            {showSuperseded ? '▾' : '▸'} {superseded.length} superseded, replaced by a later confirmation
          </Button>

          {showSuperseded && superseded.map(sp => {
            const when = sp.superseded_at ? new Date(sp.superseded_at) : null;
            const dt = eventDay(sp.event_date, { weekday: 'short', day: 'numeric', month: 'short' });
            return (
              <div key={sp.id} className="ep-old">
                <div style={{ minWidth: 0 }}>
                  <div className="ep-old__title">{sp.title}{sp.event_time ? ` · ${sp.event_time}` : ''}</div>
                  <div className="ep-card__meta">
                    {dt}
                    {sp.superseded_by_title ? ` · replaced by "${sp.superseded_by_title}"` : ' · the event was cancelled'}
                    {when ? ` · ${when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                  </div>
                </div>
                <Button size="sm" variant="secondary" disabled={restoring === sp.id} onClick={() => restore(sp.id)}>
                  {restoring === sp.id ? 'Restoring…' : 'Restore'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const close = () => setPanelOpen(false);
  const title = 'Events spotted in your emails';

  return (
    <>
      <div className="ep-banner" data-testid="event-suggestions-banner">
        <Banner tone="info" action={<Button size="sm" variant="secondary" onClick={() => setPanelOpen(true)}>Review</Button>}>
          {summary}
        </Banner>
      </div>

      {phone ? (
        <Sheet open={panelOpen} onClose={close} title={title}>{body}</Sheet>
      ) : (
        <SidePanel open={panelOpen} onClose={close} title={title} color="pastel-yellow">{body}</SidePanel>
      )}

      <ConfirmDialog
        open={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        onConfirm={() => dismissMany(selected.size ? [...selected] : null)}
        title={`Dismiss ${dismissCount} suggestion${dismissCount === 1 ? '' : 's'}?`}
        message="This cannot be undone. Dismissed suggestions do not come back."
        confirmLabel="Yes, dismiss"
      />
    </>
  );
}

// Fresh-eyes 17: one suggestion. What the card shows is the event, when, who sent it, and the
// actions; the source (the snippet, the subject, the thread, the privacy note) is behind "Show
// source". "Open the full email" is a link to the email's web version and shows only when the
// mail provider has one (IMAP has none, and saying so on every card was noise).
function ProposalCard({ p, busy, selected, onToggle, rs, onAccept, onResolve, onCancelMove }) {
  const [showSource, setShowSource] = useState(false);
  const [ref, setRef] = useState(null);   // { known, reference, thread } from the message store

  // The store only (no provider call), so it is safe on every card: it says whether there is a
  // web version to link to, and it is what "Show source" shows.
  useEffect(() => {
    let alive = true;
    if (!p.source_email_reference) { setRef({ known: false, reference: null, thread: null }); return undefined; }
    const qs = p.connection_id ? `?connectionId=${encodeURIComponent(p.connection_id)}` : '';
    api(`/api/email/message-ref/${encodeURIComponent(p.source_email_reference)}${qs}`)
      .then(r => r.json())
      .then(d => { if (alive) setRef({ known: !!d.known, reference: d.reference || null, thread: d.thread || null }); })
      .catch(() => { if (alive) setRef({ known: false, reference: null, thread: null }); });
    return () => { alive = false; };
  }, [p.source_email_reference, p.connection_id]);

  const dateStr = eventDay(p.event_date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const already = p.already_added || null;
  const clash = p.clash_result || {};
  const clashes = (!already && clash.checked !== false && Array.isArray(clash.clashes)) ? clash.clashes : [];
  const hasClash = clashes.length > 0;
  const clashText = clashes.map(c =>
    c.source === 'booking' ? `booking "${c.title}"` : c.source === 'calendar' ? `"${c.title}" (${c.label || 'Calendar'})` : `"${c.title}"`
  ).join(', ');
  // confidence arrives as a string (::text from the query); everything stored cleared the 0.6
  // extraction floor, so this flags the bottom of that range.
  const confNum = p.confidence == null ? null : parseFloat(p.confidence);
  const lowConfidence = confNum != null && !isNaN(confNum) && confNum < 0.75;
  const sender = p.source_sender || ref?.reference?.fromName || ref?.reference?.fromEmail || null;
  const openUrl = ref?.reference?.openUrl || null;

  return (
    <article className="ep-card" data-testid={`proposal-${p.id}`}>
      <Checkbox checked={selected} onChange={onToggle}
        label={<span className="ep-card__title">{p.title}</span>} />
      <div className="ep-card__when">
        {`${dateStr}${p.event_time ? ` · ${p.event_time}` : ''}`}
        {lowConfidence && <span className="ep-card__meta"> · less certain</span>}
      </div>
      {/* Block 218: a proposed date must not read like a settled one. */}
      {p.certainty === 'proposed' && (
        <div className="ep-card__note">
          <StatusPill tone="warning">Date not confirmed</StatusPill>
          <span>still being arranged, check before you rely on it</span>
        </div>
      )}
      {sender && <div className="ep-card__meta">From {sender}</div>}

      {/* Fresh-eyes 18: already in a connected calendar is not a new event and not a clash. */}
      {already && (
        <div className="ep-card__note">
          <StatusPill tone="success">Already in your calendar</StatusPill>
        </div>
      )}

      {/* Block 208: an event that needs a response, with the link to do it. */}
      {!already && p.requires_action && (
        <div className="ep-card__note">
          <strong>
            {p.action_type === 'ticket_purchase' ? '🎟 Tickets needed' : p.action_type === 'rsvp' ? '✉️ RSVP needed' : '⚑ Needs a response'}
          </strong>
          {p.booking_url && (
            <a href={p.booking_url} target="_blank" rel="noopener noreferrer">
              {p.action_type === 'ticket_purchase' ? 'Book tickets' : 'Respond'} ↗
            </a>
          )}
        </div>
      )}
      {!already && !p.requires_action && p.booking_url && (
        <div className="ep-card__note">
          <a href={p.booking_url} target="_blank" rel="noopener noreferrer">Booking link ↗</a>
        </div>
      )}

      {hasClash && <div className="ep-card__clash">⚠ Clashes with {clashText}</div>}

      {rs === 'noslot' && (
        <div className="ep-card__note">No free slot found nearby to move the existing item. Add anyway or skip?</div>
      )}
      {rs && rs !== 'noslot' && (
        <div className="ep-card__note">Move "{rs.existingTitle}" to {formatTaskWhen(rs.suggestedStart)}?</div>
      )}

      <div className="ep-card__actions">
        {already && (
          <Button size="sm" variant="tertiary" disabled={busy} onClick={() => onResolve('skip')}>Dismiss</Button>
        )}
        {!already && !hasClash && (
          <>
            <Button size="sm" variant="confirm" disabled={busy} onClick={onAccept}>Add to calendar</Button>
            <Button size="sm" variant="tertiary" disabled={busy} onClick={() => onResolve('skip')}>Ignore</Button>
          </>
        )}
        {hasClash && !rs && (
          <>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onResolve('move_existing')}>Move existing item</Button>
            <Button size="sm" variant="confirm" disabled={busy} onClick={() => onResolve('add_anyway')}>Add anyway</Button>
            <Button size="sm" variant="tertiary" disabled={busy} onClick={() => onResolve('skip')}>Skip this one</Button>
          </>
        )}
        {hasClash && rs && rs !== 'noslot' && (
          <>
            <Button size="sm" variant="confirm" disabled={busy} onClick={() => onResolve('move_existing', true)}>Confirm move</Button>
            <Button size="sm" variant="tertiary" disabled={busy} onClick={onCancelMove}>Cancel</Button>
          </>
        )}
        {hasClash && rs === 'noslot' && (
          <>
            <Button size="sm" variant="confirm" disabled={busy} onClick={() => onResolve('add_anyway')}>Add anyway</Button>
            <Button size="sm" variant="tertiary" disabled={busy} onClick={() => onResolve('skip')}>Skip this one</Button>
          </>
        )}
      </div>

      <div className="ep-card__source">
        <Button size="sm" variant="tertiary" aria-expanded={showSource} onClick={() => setShowSource(v => !v)}>
          {showSource ? 'Hide source' : 'Show source'}
        </Button>
        {openUrl && (
          <a className="ep-card__open" href={openUrl} target="_blank" rel="noopener noreferrer">Open the full email ↗</a>
        )}
      </div>
      {showSource && (
        <div className="ep-card__sourcebody">
          {p.event_type && p.event_type !== 'other' && (
            <div className="ep-card__meta">{p.event_type.replace(/_/g, ' ')}</div>
          )}
          {/* Block 236: the message reference, embedded under the card. */}
          <MessageReference
            messageId={p.source_email_reference}
            connectionId={p.connection_id}
            fallbackSender={p.source_sender}
            fallbackSubject={p.source_subject}
            preloaded={ref}
          />
        </div>
      )}
    </article>
  );
}
